import React, { useState } from "react";
import {
  Annotation,
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { selectFormat } from "components/Utils/Utils";
import { scaleLinear } from "d3-scale";

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const offsets = {
  VT: [50, -8],
  NH: [34, 2],
  MA: [30, -1],
  RI: [28, 2],
  CT: [35, 10],
  NJ: [34, 1],
  DE: [33, 0],
  MD: [47, 10],
  DC: [49, 21],
};

const allStates = [
  { id: "AL", val: "01" },
  { id: "AK", val: "02" },
  { id: "AS", val: "60" },
  { id: "AZ", val: "04" },
  { id: "AR", val: "05" },
  { id: "CA", val: "06" },
  { id: "CO", val: "08" },
  { id: "CT", val: "09" },
  { id: "DE", val: "10" },
  { id: "DC", val: "11" },
  { id: "FL", val: "12" },
  { id: "FM", val: "64" },
  { id: "GA", val: "13" },
  { id: "GU", val: "66" },
  { id: "HI", val: "15" },
  { id: "ID", val: "16" },
  { id: "IL", val: "17" },
  { id: "IN", val: "18" },
  { id: "IA", val: "19" },
  { id: "KS", val: "20" },
  { id: "KY", val: "21" },
  { id: "LA", val: "22" },
  { id: "ME", val: "23" },
  { id: "MH", val: "68" },
  { id: "MD", val: "24" },
  { id: "MA", val: "25" },
  { id: "MI", val: "26" },
  { id: "MN", val: "27" },
  { id: "MS", val: "28" },
  { id: "MO", val: "29" },
  { id: "MT", val: "30" },
  { id: "NE", val: "31" },
  { id: "NV", val: "32" },
  { id: "NH", val: "33" },
  { id: "NJ", val: "34" },
  { id: "NM", val: "35" },
  { id: "NY", val: "36" },
  { id: "NC", val: "37" },
  { id: "ND", val: "38" },
  { id: "MP", val: "69" },
  { id: "OH", val: "39" },
  { id: "OK", val: "40" },
  { id: "OR", val: "41" },
  { id: "PW", val: "70" },
  { id: "PA", val: "42" },
  { id: "PR", val: "72" },
  { id: "RI", val: "44" },
  { id: "SC", val: "45" },
  { id: "SD", val: "46" },
  { id: "TN", val: "47" },
  { id: "TX", val: "48" },
  { id: "UM", val: "74" },
  { id: "UT", val: "49" },
  { id: "VT", val: "50" },
  { id: "VA", val: "51" },
  { id: "VI", val: "78" },
  { id: "WA", val: "53" },
  { id: "WV", val: "54" },
  { id: "WI", val: "55" },
  { id: "WY", val: "56" },
];

const cityScale = scaleLinear().domain([0, 37843000]).range([1.5, 30]);

const HearstMap = (props) => {
  let { data } = props;

  data = [
    { state: "AR", zip: "72762", value: 71940, coordinates: [-94.194, 36.186] },
    { state: "AR", zip: "72701", value: 22, coordinates: [-94.134, 35.504] },
    { state: "GA", zip: "30605", value: 36752, coordinates: [-83.345, 33.925] },
    { state: "GA", zip: "30606", value: 26586, coordinates: [-83.417, 33.646] },
    { state: "NY", zip: "11220", value: 46154, coordinates: [-74.016, 41.641] },
  ];

  const colorScale = scaleLinear()
    .domain([22, 71940])
    .range(["#f9e0dc", "#fc6d52"]);

  const [tooltipContent, setTooltipContent] = useState(null);

  const renderStates = (props) => {
    const { geographies, setTooltipContent } = props;

    const max = Math.max(...data.map((d) => d.value));
    cityScale.domain([0, max]);
    return (
      <>
        {geographies.map((geo) => (
          <Geography
            key={geo.rsmKey}
            stroke="#FFF"
            geography={geo}
            fill={
              data.find(
                (state) =>
                  state.state === allStates.find((s) => s.val === geo.id).id
              )
                ? "#f7a691"
                : "#DDD"
            }
          />
        ))}
        {geographies.map((geo) => {
          const centroid = geoCentroid(geo);
          const cur = allStates.find((s) => s.val === geo.id);
          return (
            <g key={geo.rsmKey + "-name"}>
              {cur &&
                centroid[0] > -160 &&
                centroid[0] < -67 &&
                (Object.keys(offsets).indexOf(cur.id) === -1 ? (
                  <Marker coordinates={centroid}>
                    <text
                      y="2"
                      fontSize={10}
                      fontFamily="Museo Slab W01"
                      color="#393939"
                      textAnchor="middle"
                    >
                      {cur.id}
                    </text>
                  </Marker>
                ) : (
                  <Annotation
                    subject={centroid}
                    dx={offsets[cur.id][0]}
                    dy={offsets[cur.id][1]}
                  >
                    <text
                      x={4}
                      fontSize={10}
                      fontFamily="Museo Slab W01"
                      color="#393939"
                      alignmentBaseline="middle"
                    >
                      {cur.id}
                    </text>
                  </Annotation>
                ))}
            </g>
          );
        })}
        {props.data.map((sData, i) => {
          const { zip, value, coordinates } = sData;
          return (
            <Marker
              key={i}
              coordinates={coordinates}
              onMouseEnter={() => {
                setTooltipContent(
                  `<b>Zip Code:</b> ${zip} <br /> <b>Impressions:</b> ${selectFormat(
                    "number",
                    value
                  )}`
                );
              }}
              onMouseLeave={() => {
                setTooltipContent("");
              }}
            >
              <circle
                r={cityScale(value)}
                fill={colorScale(value)}
                stroke="#fff"
                strokeWidth={2}
                data-tooltip-id={`markerTooltip${i}`}
                data-tooltip-html={tooltipContent}
              />
              <ReactTooltip
                id={`markerTooltip${i}`}
                className="title-tooltip-header"
              />
            </Marker>
          );
        })}
      </>
    );
  };

  return (
    <div className="col-12 mt-2 pl-3">
      <ComposableMap data-tooltip-html="" projection="geoAlbersUsa">
        <Geographies geography={geoUrl}>
          {(props) =>
            renderStates({
              ...props,
              setTooltipContent: setTooltipContent,
              data: data,
            })
          }
        </Geographies>
      </ComposableMap>
    </div>
  );
};

export default HearstMap;
